import { AlertTriangle, CalendarClock, Clock3, Columns3, Plus, UserCog } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { EmptyState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { PROJECT_WORK_PERM } from '../../permissions'
import type { Installation, InstallationKind, Project } from '../../types'
import { toastError } from '../../ui'
import { useDeleteInstallation } from '../../workApi'
import { InstallationDetail } from './InstallationDetail'
import { InstallationFormModal } from './InstallationFormModal'
import { InstallationSidebar } from './InstallationSidebar'
import { InstallationTable } from './InstallationTable'

const FILTERS = ['all', 'active', 'demo', 'blocked', 'approval', 'done'] as const
type Filter = (typeof FILTERS)[number]
const MATCH: Record<Filter, (i: Installation) => boolean> = {
  all: (i) => i.status !== 'done',
  active: (i) => i.kind === 'installation' && i.status !== 'done',
  demo: (i) => i.kind === 'demo' && i.status !== 'done',
  blocked: (i) => i.status === 'blocked' || i.blockers.some((b) => !b.resolved && b.severity === 'high'),
  approval: (i) => i.status === 'awaiting_approval',
  done: (i) => i.status === 'done',
}

function Kpi({ icon: Icon, value, label, hint, tone }: { icon: typeof Clock3; value: number; label: string; hint: string; tone: string }) {
  return (
    <div className={cn('flex items-start gap-3 rounded-xl border p-4', tone)}>
      <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-card shadow-card">
        <Icon className="size-5" aria-hidden />
      </span>
      <div className="min-w-0">
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        <div className="text-sm font-medium text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
    </div>
  )
}

export function InstallationsTab({ project, onOpenBoard }: { project: Project; onOpenBoard: (installationId: string) => void }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const remove = useDeleteInstallation(project.id)
  const [params, setParams] = useSearchParams()
  const [filter, setFilter] = useState<Filter>('all')
  const [dialog, setDialog] = useState<{ open: boolean; item: Installation | null; kind: InstallationKind }>({ open: false, item: null, kind: 'installation' })
  const canWrite = can(PROJECT_WORK_PERM)

  const list = project.installations
  const rows = list.filter(MATCH[filter])
  const selected = list.find((i) => i.id === params.get('i')) ?? rows[0] ?? null
  const select = (id: string) => setParams({ i: id }, { replace: true })

  const onDelete = async (i: Installation) => {
    const ok = await confirmModal({
      title: t('projects.install.deleteTitle'),
      description: t('projects.install.deleteBody', { name: i.customer, stages: i.stages.length }),
      confirmLabel: t('common.delete'),
      destructive: true,
    })
    if (!ok) return
    try {
      await remove.mutateAsync(i.id)
      toast.success(t('projects.install.deleted'))
    } catch (e) {
      toastError(e)
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_14rem]">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Kpi icon={UserCog} value={list.filter(MATCH.active).length} label={t('projects.install.kpi.active')} hint={t('projects.install.kpi.activeHint')} tone="border-success/20 bg-success/[0.06] [&_svg]:text-success" />
          <Kpi icon={CalendarClock} value={list.filter(MATCH.demo).length} label={t('projects.install.kpi.demo')} hint={t('projects.install.kpi.demoHint')} tone="border-info/20 bg-info/[0.06] [&_svg]:text-info" />
          <Kpi icon={AlertTriangle} value={list.filter(MATCH.blocked).length} label={t('projects.install.kpi.blocked')} hint={t('projects.install.kpi.blockedHint')} tone="border-warning/30 bg-warning/[0.08] [&_svg]:text-warning" />
          <Kpi icon={Clock3} value={list.filter(MATCH.approval).length} label={t('projects.install.kpi.approval')} hint={t('projects.install.kpi.approvalHint')} tone="border-violet/20 bg-violet/[0.06] [&_svg]:text-violet" />
        </div>
        {canWrite ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1 lg:content-center">
            <Button onClick={() => setDialog({ open: true, item: null, kind: 'installation' })}>
              <Plus />
              {t('projects.install.start')}
            </Button>
            <Button variant="outline" onClick={() => setDialog({ open: true, item: null, kind: 'demo' })}>
              <Plus />
              {t('projects.install.planDemo')}
            </Button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)} className="min-w-0 max-w-full">
          <TabsList>
            {FILTERS.map((f) => (
              <TabsTrigger key={f} value={f}>
                {t(`projects.install.filter.${f}`, { count: list.filter(MATCH[f]).length })}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {selected ? (
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => onOpenBoard(selected.id)}>
            <Columns3 />
            {t('projects.install.openBoard')}
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-4">
          {rows.length ? (
            <InstallationTable rows={rows} selectedId={selected?.id} onSelect={select} canWrite={canWrite} onEdit={(item) => setDialog({ open: true, item, kind: item.kind })} onDelete={(i) => void onDelete(i)} />
          ) : (
            <div className="rounded-xl border bg-card shadow-card">
              <EmptyState title={t('projects.install.empty')} />
            </div>
          )}
          {selected ? <InstallationDetail key={selected.id} project={project} installation={selected} canWrite={canWrite} /> : null}
        </div>
        {selected ? <InstallationSidebar key={selected.id} project={project} installation={selected} canWrite={canWrite} /> : null}
      </div>

      <InstallationFormModal
        open={dialog.open}
        project={project}
        installation={dialog.item}
        defaultKind={dialog.kind}
        onSaved={select}
        onOpenChange={(open) => setDialog((s) => ({ ...s, open }))}
      />
    </div>
  )
}
